import {useMemo, useState, useEffect} from "react";
import type {ReactNode} from "react";
import {useCurrentRefinements, useInstantSearch} from "react-instantsearch";
import {useConfigContext} from "@/ConfigProvider";
import {useInstantSearchUIContext} from "@/instantsearch/InstantSearchUIProvider";
import {useIsMobile} from "@/hooks/useIsMobile";
import FilterBox from "@/instantsearch/components/FilterBox";
import ResetFilters from "@/instantsearch/components/ResetFilters";
import AisActiveRefinements from "@/instantsearch/components/ais-active-refinements";

type SidebarMenuProps = {
    controls?: ReactNode;
};

export function SidebarMenu({controls}: SidebarMenuProps) {
    const {isActive, toggle, panelRef, closePanel, openPanel} = useInstantSearchUIContext();
    const {tc, algoliaRefinements: {refinementList}} = useConfigContext();
    const {items} = useCurrentRefinements();
    const {
        results: {nbHits},
    } = useInstantSearch();
    const activeFilters = useMemo(() => {
        return items.reduce((total, item) => total + item.refinements.length, 0);
    }, [items]);
    const openFilter = () => {
        if (isActive) {
            closePanel();
        } else {
            openPanel();
        }
    }
    const isMobile = useIsMobile();
    const [visualState, setVisualState] = useState<'closed' | 'open' | 'closing'>('closed');

    useEffect(() => {
        if (isActive) {
            setVisualState('open');
        } else if (!isMobile) {

            setVisualState('closed');
        } else {
            setVisualState((prev) => prev === 'open' ? 'closing' : prev);
        }
    }, [isActive, isMobile]);

    const handleTransitionEnd = () => {
        if (visualState === 'closing') setVisualState('closed');
    };

    if(refinementList.length === 0) return null;

    const isVisible = visualState !== 'closed';

    return (
        <div className={`algoliaSearchSidebarMenu ${isVisible ? "is-active" : ""} ${visualState === 'open' ? "is-open" : ""}`}
             aria-hidden={!isActive}
             onTransitionEnd={handleTransitionEnd}
             id="filterSidebar" aria-labelledby="filterToggle" tabIndex={-1} ref={panelRef}>
            <button className={"algoliaFilterBoxToggle algoliaSearchSidebarMenu__toggle"}
                    id={"filterToggle"}
                    aria-controls={"filterSidebar"}
                    aria-expanded={isActive}
                    onClick={() => {
                        openFilter()
                    }}
            >
                <i className={`icon icon-close`} aria-hidden={true}></i>
            </button>
            <div className={"algoliaSearchSidebarMenu__container"}>
                <section className={"algoliaSearchSidebarMenu__content"}>
                    <FilterBox/>
                    {controls && (
                        <div className={"algoliaSearchSidebarMenu__controls"}>
                            {controls}
                        </div>
                    )}
                </section>
                <div className="algoliaSearchSidebarMenu__activeFilters">
                    <ResetFilters/>
                    <AisActiveRefinements/>
                </div>
                <footer className={"algoliaSearchSidebarMenu__footer"}>
                    {(nbHits > 0) &&
                        <button className={"button button--primary"} onClick={() => toggle()}>
                            {nbHits} {tc.showHits}
                        </button>
                    }
                </footer>
            </div>
        </div>
    )
}